import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Radio from "@mui/material/Radio";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import "../login/style.css";
import "./style.css";

import Input from "../bannerInput";
import Button from "../button";
import VerifyCode from "../verifyCode";
import PhoneNumber from "../phoneInput";
import { register } from "api/Auth";
import { MenuItem, Typography } from "@mui/material";
import { useTranslation } from "i18n";

const SignUp = ({ handleClose }) => {
  const { t } = useTranslation();
  const [err, setErr] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [checked, setChecked] = useState(false);

  const [email, setEmail] = useState("");

  const phoneRegExp =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required().label(t("account.firstName")),
    lastName: Yup.string().required().label(t("account.lastName")),
    email: Yup.string().email().required().label(t("account.email")),
    password: Yup.string().min(3).required().label(t("auth.password")),
    heardFrom: Yup.string().required().label(t("auth.heardFrom")),
    otherField: Yup.string().when("heardFrom", {
      is: "Other", // Condition when heardFrom is "Other"
      then: Yup.string()
        .required(t("auth.otherFieldRequired"))
        .label(t("auth.thisField")),
      otherwise: Yup.string().nullable(), // Optional or non-required when the condition does not match
    }),
  });

  const handelSubmit = async (values) => {
    setLoading(true);
    setErr("");
    values.phoneNumber = `+${values.phoneNumber}`;
    if (values.heardFrom === "Other") {
      values.heardFrom = values.otherField;
    }

    delete values.otherField;
    const res = await register({ body: values });
    if (!res.ok) {
      setErr(res?.data?.errorDetails);
    } else {
      setShowOtp(true);

      setEmail(values.email);
    }
    setLoading(false);
  };

  return (
    <>
      {!showOtp ? (
        <div className="login-modal">
          <div className="login-modal__glow" />
          <p className="login-modal__brand">{t("home.appBrand")}</p>
          <h1 className="login-modal__title">{t("auth.createAccount")}</h1>
          <p className="login-modal__subtitle">{t("auth.registerSubtitle")}</p>
          {err && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "14px" }}>
              {err}
            </Alert>
          )}
          <Formik
            initialValues={{
              firstName: "",
              lastName: "",
              email: "",
              password: "",
              phoneNumber: "",
              heardFrom: "",
              otherField: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handelSubmit}
          >
            {({
              handleChange,
              handleSubmit,
              errors,
              setFieldTouched,
              touched,
              values,
              setFieldValue,
            }) => (
              <>
                <div className="login-modal__row">
                  <Input
                    mandatory
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setFieldValue("firstName", value.toLowerCase());
                    }}
                    error={touched.firstName && errors.firstName}
                    onBlur={() => setFieldTouched("firstName")}
                    name={"firstName"}
                    label={t("auth.enterFirstName")}
                  />
                  <Input
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setFieldValue("lastName", value.toLowerCase());
                    }}
                    error={touched.lastName && errors.lastName}
                    onBlur={() => setFieldTouched("lastName")}
                    name={"lastName"}
                    mandatory
                    label={t("auth.enterLastName")}
                  />
                </div>
                <Input
                  onChange={(e) => {
                    const value = e.target.value || "";
                    setFieldValue("email", value.toLowerCase());
                  }}
                  error={touched.email && errors.email}
                  onBlur={() => setFieldTouched("email")}
                  name={"email"}
                  mandatory
                  type="email"
                  label={t("auth.enterEmail")}
                />
                <PhoneNumber
                  name={`phoneNumber`}
                  onChange={handleChange(`phoneNumber`)}
                  error={touched.phoneNumber && errors.phoneNumber}
                  mandatory
                  onBlur={() => setFieldTouched("phoneNumber")}
                  setCode={setCode}
                  label={t("auth.enterPhoneNumber")}
                />
                <Input
                  onChange={handleChange(`password`)}
                  error={touched.password && errors.password}
                  onBlur={() => setFieldTouched("password")}
                  name={"password"}
                  mandatory
                  type="password"
                  label={t("auth.enterPassword")}
                />

                <label className="login-modal__select-label">
                  {t("auth.heardAboutUs")}
                </label>

                <TextField
                  className="auth-select"
                  onChange={handleChange(`heardFrom`)}
                  error={touched.heardFrom && errors.heardFrom}
                  helperText={touched.heardFrom && errors.heardFrom}
                  onBlur={() => setFieldTouched("heardFrom")}
                  name={"heardFrom"}
                  select
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                  size="small"
                >
                  <MenuItem value={"Google"}>Google</MenuItem>
                  <MenuItem value={"Facebook"}>Facebook</MenuItem>
                  <MenuItem value={"Instagram"}>Instagram</MenuItem>
                  <MenuItem value={"Referral from a Friend"}>
                    {t("auth.referralFriend")}
                  </MenuItem>
                  <MenuItem value={"TV or Radio Ad"}>{t("auth.tvRadioAd")}</MenuItem>
                  <MenuItem value={"Newspaper or Magazine"}>
                    {t("auth.newspaperMagazine")}
                  </MenuItem>
                  <MenuItem value={"Flyer or Brochure"}>
                    {t("auth.flyerBrochure")}
                  </MenuItem>
                  <MenuItem value={"Other"}>{t("auth.other")}</MenuItem>
                </TextField>
                {values["heardFrom"] === "Other" && (
                  <>
                    <label className="login-modal__select-label">
                      {t("auth.pleaseSpecify")}
                    </label>

                    <TextField
                      className="auth-select"
                      onChange={handleChange(`otherField`)}
                      error={touched.otherField && errors.otherField}
                      helperText={touched.otherField && errors.otherField}
                      onBlur={() => setFieldTouched("otherField")}
                      name={"otherField"}
                      fullWidth
                      variant="outlined"
                      sx={{ mb: 2 }}
                      size="small"
                    />
                  </>
                )}
                <Box className="login-modal__agree">
                  <Radio
                    checked={checked}
                    onClick={() => setChecked((prev) => !prev)}
                  />
                  <Typography className="login-modal__agree-text">
                    {t("auth.agreeTo")}{" "}
                    <Typography
                      component="a"
                      href="/privacy-policy"
                      variant="subtitle2"
                      fontWeight="bold"
                      color="var(--primary-color)"
                      target="_blank"
                    >
                      {t("auth.privacyPolicy")}
                    </Typography>
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={!checked}
                  className="bigBtn w-100 primary-color"
                >
                  {loading ? (
                    <CircularProgress sx={{ color: "#fff" }} size={22} />
                  ) : (
                    t("navigation.signUp")
                  )}
                </Button>
              </>
            )}
          </Formik>
          <p className="login-modal__footer">
            {t("auth.alreadyHaveAccount")}{" "}
            <button
              type="button"
              className="login-modal__link"
              onClick={() => {
                handleClose();
                window.location.hash = "#auth-required";
              }}
            >
              {t("navigation.logIn")}
            </button>
          </p>
        </div>
      ) : (
        <VerifyCode email={email} handleClose={handleClose} />
      )}
    </>
  );
};

export default SignUp;
